/**
 * Date/time primitives — single source of truth (SSOT).
 *
 * Two fundamentally different kinds of timestamps live in the FerryGate
 * payloads, and they must NEVER be formatted the same way:
 *
 *  1. **Absolute instants** (`expire_at`, `paid_at`, `created_at`, event
 *     timeline dates). These designate a precise moment in time. The agent
 *     must see them in *their own* timezone — an option that expires at the
 *     same instant should read identically on every screen for a given agent.
 *     → use `parseInstant` / `formatInstant` (TZ-aware, rendered via
 *       `toLocaleString` in the browser/agent locale & timezone).
 *
 *  2. **Wall-clock / port-local times** (a ferry departs at 22:30 *port time*).
 *     The number printed on the boarding pass is the truth; the offset the
 *     serializer attaches is unreliable (often `+00:00` even for a +02:00
 *     port). We must show the exact HH:MM components verbatim and never let
 *     `Date()` / `Intl` re-shift them by the browser timezone.
 *     → use `formatPortDate` / `formatPortTime` (string parsing only).
 *
 * Decision (2026-06-14): `expire_at` is an absolute instant, displayed in the
 * agent timezone. This file is the only place that logic lives — Confirmation
 * and SideTicket both consume it, so an expiry can no longer read 17:01 on one
 * screen and 15:01 on the other.
 */

/** Matches the leading `YYYY-MM-DD( |T)HH:MM[:SS]` of an ISO-ish string. */
const ISO_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/

export interface FormatInstantOptions {
  /** `Intl` date style. Pass `'none'` to omit the date part entirely. */
  dateStyle?: 'full' | 'long' | 'medium' | 'short' | 'none'
  /** `Intl` time style. Pass `'none'` to omit the time part entirely. */
  timeStyle?: 'full' | 'long' | 'medium' | 'short' | 'none'
  /** BCP-47 locale. Defaults to the runtime default (agent browser locale). */
  locale?: string
  /** IANA timezone. Defaults to the runtime default (agent timezone). */
  timeZone?: string
}

/**
 * Parse an absolute instant into epoch milliseconds, or `null` if invalid.
 *
 * Accepts:
 *  - epoch numbers (10-digit → seconds, otherwise milliseconds),
 *  - `Date` objects,
 *  - ISO-ish strings. A space separator is normalised to `T` so the native
 *    parser is deterministic. A string carrying an explicit offset is parsed
 *    as that absolute instant; a string without offset is parsed as local
 *    time (legacy backend behaviour — both consuming views now agree because
 *    they share this function).
 */
export function parseInstant(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null

  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null
    const ms = String(Math.trunc(raw)).length <= 10 ? raw * 1000 : raw
    return Number.isFinite(ms) ? ms : null
  }

  if (raw instanceof Date) {
    const ms = raw.getTime()
    return Number.isFinite(ms) ? ms : null
  }

  if (typeof raw === 'string') {
    const s = raw.trim()
    if (!s) return null
    // Normalise "YYYY-MM-DD HH:MM" → "YYYY-MM-DDTHH:MM" so Date.parse is
    // deterministic across engines. Leave an explicit offset untouched.
    const normalised = ISO_DATETIME_RE.test(s) ? s.replace(' ', 'T') : s
    const ms = Date.parse(normalised)
    return Number.isFinite(ms) ? ms : null
  }

  return null
}

/**
 * Format an absolute instant in the agent's locale & timezone.
 * Returns the `fallback` (default `'—'`) for unparseable input.
 */
export function formatInstant(raw: unknown, opts: FormatInstantOptions = {}, fallback = '—'): string {
  const ms = parseInstant(raw)
  if (ms === null) return fallback

  const intlOpts: Intl.DateTimeFormatOptions = {}
  const dateStyle = opts.dateStyle ?? 'short'
  const timeStyle = opts.timeStyle ?? 'short'
  if (dateStyle !== 'none') intlOpts.dateStyle = dateStyle
  if (timeStyle !== 'none') intlOpts.timeStyle = timeStyle
  if (opts.timeZone) intlOpts.timeZone = opts.timeZone

  try {
    return new Date(ms).toLocaleString(opts.locale, intlOpts)
  } catch {
    return new Date(ms).toLocaleString()
  }
}

/**
 * Extract the verbatim wall-clock time `HH:MM` from a port-local datetime.
 * Reads the string components directly — never goes through `Date`/`Intl`, so
 * a `22:30` departure always renders `22:30` regardless of the browser
 * timezone or the (unreliable) offset the backend attached.
 *
 * Also accepts a bare `HH:MM[:SS]` time. Returns `fallback` if unrecognised.
 */
export function formatPortTime(raw: unknown, fallback = ''): string {
  if (typeof raw !== 'string') return fallback
  const s = raw.trim()
  if (!s) return fallback

  const timeOnly = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(s)
  if (timeOnly) return `${timeOnly[1]}:${timeOnly[2]}`

  const dt = ISO_DATETIME_RE.exec(s)
  if (dt) return `${dt[4]}:${dt[5]}`

  return fallback
}

/**
 * Format the verbatim port-local date. By default `DD/MM/YYYY`; pass a
 * `weekday: true` option to get a localized "weekday day month" form
 * (e.g. "sam. 26 juin") built from the verbatim Y/M/D — the time-of-day is
 * irrelevant here so no timezone shift can occur.
 */
export function formatPortDate(
  raw: unknown,
  opts: { weekday?: boolean; locale?: string } = {},
  fallback = '',
): string {
  if (typeof raw !== 'string') return fallback
  const s = raw.trim()
  const m = ISO_DATETIME_RE.exec(s) || /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (!m) return fallback
  const [, y, mo, d] = m

  if (!opts.weekday) return `${d}/${mo}/${y}`

  // Build a local Date at midday purely to obtain the weekday/month labels;
  // the date components are verbatim, so this is shift-safe.
  const dt = new Date(Number(y), Number(mo) - 1, Number(d), 12, 0, 0)
  if (!Number.isFinite(dt.getTime())) return `${d}/${mo}/${y}`
  try {
    return dt.toLocaleDateString(opts.locale, { weekday: 'short', day: 'numeric', month: 'short' })
  } catch {
    return `${d}/${mo}/${y}`
  }
}

/**
 * Whole minutes between two absolute instants (`to - from`), or `null` if
 * either is unparseable. Negative/zero spans return `null` (a ferry never
 * arrives before it departs — a non-positive value means bad data).
 */
export function diffDuration(fromRaw: unknown, toRaw: unknown): number | null {
  const from = parseInstant(fromRaw)
  const to = parseInstant(toRaw)
  if (from === null || to === null) return null
  const minutes = Math.round((to - from) / 60_000)
  return minutes > 0 ? minutes : null
}

/**
 * Format a birth date to dd/mm/yyyy, whatever the input shape:
 *  - ISO `yyyy-mm-dd` (e.g. provider/widget) → `dd/mm/yyyy`
 *  - `dd-mm-yyyy` → `dd/mm/yyyy`
 *  - already `dd/mm/yyyy` → unchanged
 * Returns '' for empty input, the raw string if unrecognised (no data loss).
 */
export function formatBirthDate(raw: unknown): string {
  if (raw === null || raw === undefined) return ''
  const s = String(raw).trim()
  if (!s) return ''
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmy) return `${dmy[1]}/${dmy[2]}/${dmy[3]}`
  return s
}
